import * as React from "react";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { useBlogTags } from "@/hooks/use-blog";

interface CategoriesTagsSectionProps {
  categories: string[];
  tags: string[];
  onCategoriesChange: (categories: string[]) => void;
  onTagsChange: (tags: string[]) => void;
}

export function CategoriesTagsSection({
  categories,
  tags,
  onCategoriesChange,
  onTagsChange,
}: CategoriesTagsSectionProps) {
  const availableCategories = [
    "Technology",
    "Web Development",
    "CMS",
    "AI & Machine Learning",
    "Productivity",
    "Business",
    "Education",
    "Marketing"
  ];

  const [tagInput, setTagInput] = React.useState("");
  const [tagSuggestions, setTagSuggestions] = React.useState<string[]>([]);
  const [categoryInput, setCategoryInput] = React.useState("");
  const [categorySuggestions, setCategorySuggestions] = React.useState<string[]>([]);
  
  // Fetch all tags from blog posts
  const { data: allTags = [] } = useBlogTags();

  const handleCategoryToggle = (category: string) => {
    if (categories.includes(category)) {
      onCategoriesChange(categories.filter(c => c !== category));
    } else {
      onCategoriesChange([...categories, category]);
    }
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Stop propagation to prevent browser extensions from interfering
    e.stopPropagation();
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      e.stopPropagation();
      const value = tagInput.trim();
      if (value) {
        addTag(value);
      }
    }
  };

  const handleCategoryInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Stop propagation to prevent browser extensions from interfering
    e.stopPropagation();
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      e.stopPropagation();
      const value = categoryInput.trim();
      if (value) {
        addCategory(value);
      }
    }
  };

  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCategoryInput(value);
    // Filter categories based on input
    if (value.trim()) {
      const filtered = availableCategories
        .filter(cat => cat.toLowerCase().includes(value.toLowerCase()))
        .filter(cat => !categories.includes(cat)) // Exclude already added categories
        .slice(0, 5);
      setCategorySuggestions(filtered);
    } else {
      setCategorySuggestions([]);
    }
  };

  const addCategory = (category: string) => {
    if (!category) return;
    
    // Prevent duplicates
    if (categories.includes(category)) {
      setCategoryInput("");
      return;
    }

    onCategoriesChange([...categories, category]);
    setCategoryInput("");
    setCategorySuggestions([]);
  };

  const removeCategory = (categoryToRemove: string) => {
    onCategoriesChange(categories.filter(cat => cat !== categoryToRemove));
  };

  const selectCategorySuggestion = (suggestion: string) => {
    addCategory(suggestion);
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);
    // Filter tags based on input
    if (value.trim() && allTags.length > 0) {
      const filtered = allTags
        .filter(tag => tag.toLowerCase().includes(value.toLowerCase()))
        .filter(tag => !tags.includes(tag)) // Exclude already added tags
        .slice(0, 5);
      setTagSuggestions(filtered);
    } else {
      setTagSuggestions([]);
    }
  };

  const addTag = (tag: string) => {
    if (!tag) return;
    
    // Prevent duplicates
    if (tags.includes(tag)) {
      setTagInput("");
      return;
    }

    onTagsChange([...tags, tag]);
    setTagInput("");
    setTagSuggestions([]);
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const selectTagSuggestion = (suggestion: string) => {
    addTag(suggestion);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Categories & Tags</h3>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories
          </label>
          <div className="space-y-2">
            {/* Quick add input */}
            <div className="relative" data-lpignore="true" data-1p-ignore="true" data-bwignore="true">
              <div className="flex gap-2">
                <Input
                  value={categoryInput}
                  onChange={handleCategoryInputChange}
                  onKeyDown={handleCategoryInputKeyDown}
                  onBlur={() => {
                    // Add category when focus leaves input
                    if (categoryInput.trim()) {
                      addCategory(categoryInput.trim());
                    }
                  }}
                  onKeyPress={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  placeholder="Gõ category và nhấn Enter hoặc dấu phẩy để thêm..."
                  className="w-full"
                  autoComplete="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  name="category-input"
                  id="category-input"
                  type="text"
                  role="textbox"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (categoryInput.trim()) {
                      addCategory(categoryInput.trim());
                    }
                  }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium whitespace-nowrap"
                >
                  Thêm
                </button>
              </div>
              {categorySuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-auto">
                  {categorySuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => selectCategorySuggestion(suggestion)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-700"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Available categories checkboxes */}
            <div className="space-y-2 pt-2">
              {availableCategories.map((category) => (
                <label key={category} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={categories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{category}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <div className="relative" data-lpignore="true" data-1p-ignore="true" data-bwignore="true">
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                onBlur={() => {
                  // Add tag when focus leaves input
                  if (tagInput.trim()) {
                    addTag(tagInput.trim());
                  }
                }}
                onKeyPress={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Gõ tag và nhấn Enter hoặc dấu phẩy để thêm..."
                className="w-full"
                autoComplete="off"
                spellCheck="false"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                name="tag-input"
                id="tag-input"
                type="text"
                role="textbox"
              />
              <button
                type="button"
                onClick={() => {
                  if (tagInput.trim()) {
                    addTag(tagInput.trim());
                  }
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium whitespace-nowrap"
              >
                Thêm
              </button>
            </div>
            {tagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-auto">
                {tagSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => selectTagSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-700"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full flex items-center gap-1.5"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-blue-900 focus:outline-none"
                    aria-label={`Xóa tag ${tag}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {tags.length === 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Chưa có tag nào. Gõ và nhấn Enter để thêm tag.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}